// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  Authn,
  AppError as AuthnAppError,
  ErrorCode as AuthnErrorCode,
  AuthnMethod,
  AuthnViews,
  type EventPayload,
  EventType,
  FatalError,
} from '@imprivata-cloud/authn';
import {
  createSpanName,
  debug,
  error,
  ImprHeaders,
  SpanComponent,
} from '@imprivata-cloud/common';
import { ScanningElement } from '@imprivata-cloud/components';
import {
  type FeatureFlagsProp,
  SelfServiceEventTypes,
  useEventListener,
} from '@imprivata-cloud/core';
import { Banner } from '@imprivata-cloud/design-system';
import { Col, Layout, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { UserNameForm } from '@/factors/username-form';

import { getOrgPreferences, setDefaultHeader } from '../../api/api';
import {
  createClientUserSession,
  determineAuthMethod,
  lookupTenant,
} from '../../api/tenantLookupApi';
import DefaultLogofrom from '../../assets/default_logo.svg?react';
import UserHex from '../../assets/face-user.svg?react';
import PasswordHex from '../../assets/passwordHex.svg?react';
import PhoneHex from '../../assets/phone.svg?react';
import TempcodeHex from '../../assets/tempcodeHex.svg?react';
import { LoginComponent } from '../../components/login/LoginComponent';
import { NotificationBanner } from '../../components/notificationBanner/NotificationBanner';
import Separator from '../../components/separator/Separator';
import { QueryParam } from '../../constants';
import { ErrorCode as TenantErrorCode } from '../../errorHandler/constants';
import { useNotifications } from '../../errorHandler/context/Notifications';
import { errorConfiguration } from '../../errorHandler/ErrorConfig';
import { InsufficientFactors } from '../../errorHandler/error-views/insufficient-factors';
import { InformationDialog } from '../../errorHandler/error-views/no-inline-enrollment';
import { AppError } from '../../errorHandler/errors';
import { WEB_APP_NAME } from '../../shared/constants';
import {
  AuthAction,
  type AuthResource,
  type DetermineAuthActionResponse,
} from '../../shared/types';
import {
  determineResource,
  getTenantSubdomain,
  getUrlParam,
  getUrlWithTenantId,
} from '../../utils';
import { AuthnLayout, LayoutHeaders } from '../Card/authn-layout';
import './styles.less';

const performLookup = async (email: string, workflowId: string) => {
  const response = await lookupTenant(email, workflowId);
  // User is redirected to lookup only when tenantId is not known.
  const tenantList = response?.tenantList ?? [];
  if (tenantList.length === 0) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }
  return tenantList.length === 1
    ? tenantList[0]
    : tenantList.find(t => t.tenantType === 'PRIMARY');
};

const updateFaceAuthQueryParams = (
  launchAuthnUi: DetermineAuthActionResponse['launchAuthnUi'],
) => {
  if (!launchAuthnUi) return;

  const url = new URL(window.location.href);
  url.searchParams.set(
    QueryParam.ShowVideo,
    String(launchAuthnUi.faceAuthVideoStreamEnabled),
  );
  url.searchParams.set(
    QueryParam.UserCanShowLiveFeed,
    String(launchAuthnUi.userCanShowLiveFeed),
  );
  window.history.replaceState(window.history.state, '', url.toString());
};

export const getTenantIdAndTargetUrl = () => {
  const targetQueryParam = getUrlParam('target');

  let tenantId = '';
  const targetUrl = targetQueryParam || '';

  if (targetQueryParam) {
    try {
      const urlObj = new URL(targetQueryParam);
      tenantId =
        urlObj.searchParams.get('tenantId') ||
        getTenantSubdomain(targetQueryParam);
    } catch {
      tenantId = getTenantSubdomain(targetQueryParam);
    }
  }

  return { tenantId, targetUrl };
};

const determineAuthAction = async (
  email: string | undefined,
  workflowId: string,
) => {
  const targetQueryParam = getUrlParam('target');

  let { tenantId, targetUrl } = getTenantIdAndTargetUrl();

  if (!tenantId && email) {
    const tenantIdFromLookup = await performLookup(email, workflowId);
    tenantId = tenantIdFromLookup?.tenantId || '';
    targetUrl = getUrlWithTenantId(targetQueryParam || '', tenantId);
  }

  if (!tenantId) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }

  console.log('[determineAuthAction] targetUrl: ', targetUrl);

  const payload = {
    username: email,
    resource: determineResource(targetQueryParam || '') as AuthResource,
    targetUrl,
  };

  const authActionRes = await determineAuthMethod(
    payload,
    tenantId,
    workflowId,
  );

  if (
    !authActionRes ||
    (authActionRes?.action === AuthAction.START_IDP_SSO &&
      !authActionRes.startIdpSso?.startSsoUrl)
  ) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }

  return { tenantId, targetUrl, ...authActionRes }; //TODO think through the return type
};

export type AuthDetails = {
  applicationId: string;
  tenantId: string;
  username?: string;
  targetUrl: string;
};

const TenantLogin = () => {
  const { t } = useTranslation();

  const { emitError, banner, clearErrorNotifications } = useNotifications();

  const [actualView, setActualView] = useState<AuthnViews>();
  const [terminalErrorCode, setTerminalErrorCode] = useState<string>();
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [authDetails, setAuthDetails] = useState<AuthDetails>();
  const [workflowId, setWorkflowId] = useState<string>();
  const [header, setHeader] = useState<LayoutHeaders>(
    LayoutHeaders.ENROLL_CONSENT,
  );
  const [logoUrl, setLogoUrl] = useState<
    string | null | undefined | React.ReactNode
  >(undefined);
  const [customHexagonImage, setCustomHexagonImage] = useState<
    string | React.ReactNode
  >();

  const handleSubmit = useCallback(
    async (email?: string) => {
      try {
        const workflowId = uuidv4();
        setWorkflowId(workflowId);
        const authActionRes = await determineAuthAction(email, workflowId);

        if (authActionRes?.tenantId) {
          setDefaultHeader(ImprHeaders.ImprTenantId, authActionRes.tenantId);
          setHeader(LayoutHeaders.AUTHENTICATION);

          try {
            const { logoUrl } = await getOrgPreferences();
            setLogoUrl(logoUrl || <DefaultLogofrom />);
          } catch (e) {
            console.log(e);
            setLogoUrl(<DefaultLogofrom />);
          }
        }

        if (
          authActionRes?.action === AuthAction.START_IDP_SSO &&
          authActionRes?.startIdpSso?.startSsoUrl
        ) {
          window.location.href = authActionRes?.startIdpSso?.startSsoUrl;
        } else {
          updateFaceAuthQueryParams(authActionRes?.launchAuthnUi);
          setAuthDetails({
            tenantId: authActionRes?.tenantId,
            applicationId: authActionRes?.launchAuthnUi?.applicationId || '',
            username: email,
            targetUrl: authActionRes?.targetUrl || '',
          });
        }
      } catch (err) {
        console.error(err);
        emitError(TenantErrorCode.TENANT_NOT_FOUND);
      }
    },
    [emitError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: tenant lookup auto-submit should only run once when the URL already has a tenantId.
  useEffect(() => {
    const { tenantId } = getTenantIdAndTargetUrl();
    if (tenantId) {
      handleSubmit();
    }
  }, []);

  // The terminal network-error screen unmounts <Authn>, so authn no longer
  // emits ONLINE/OFFLINE events. The host tracks connectivity itself to keep
  // the network-error "Try again" button disabled while offline.
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const tracing = useMemo(
    () => ({
      workflowId: workflowId || '',
    }),
    [workflowId],
  );

  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username: authDetails?.username,
    }),
    [authDetails],
  );

  const deviceFactors = useMemo(
    () => [
      AuthnMethod.FACE_ID,
      AuthnMethod.PASSWORD,
      AuthnMethod.IMPR_ID,
      AuthnMethod.IMPR_PIN,
      AuthnMethod.TEMP_CODE,
      AuthnMethod.EMAIL_OTP,
    ],
    [],
  );
  const restartFlow = useCallback(() => {
    setLogoUrl(undefined);
    setHeader(LayoutHeaders.ENROLL_CONSENT);
    setAuthDetails(undefined);
    setTerminalErrorCode(undefined);
  }, []);

  const onSuccess = useCallback(async () => {
    try {
      debug('Processing authentication with code');
      console.log('[onSuccess] authDetails: ', authDetails);
      if (!authDetails?.targetUrl) {
        error(
          'Target URL is not defined',
          'Cannot redirect to an undefined target URL',
        );
        return;
      }
      //call create client session API
      await createClientUserSession(authDetails?.tenantId || '', workflowId);
      debug('Redirecting to target', { targetUrl: authDetails?.targetUrl });
      window.location.href = authDetails?.targetUrl;
    } catch (err) {
      error('Authentication failed', String(err));
      emitError(err instanceof AppError ? err : TenantErrorCode.INTERNAL);
      throw err;
    }
  }, [authDetails, workflowId, emitError]);

  // Handle events coming from AuthnModule
  const handleEvent = useCallback(
    ({ event, data }: EventPayload) => {
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);
      const errorCode =
        data instanceof AuthnAppError
          ? data.code
          : typeof data === 'string'
            ? data
            : undefined;
      const emitAuthnError = (fallbackCode = TenantErrorCode.INTERNAL) => {
        emitError(errorCode ?? fallbackCode);
      };
      const authMethodIcons: Partial<Record<AuthnMethod, React.ReactNode>> = {
        [AuthnMethod.FACE_ID]: <ScanningElement />,
        [AuthnMethod.PASSWORD]: <PasswordHex />,
        [AuthnMethod.IMPR_ID]: <PhoneHex />,
        [AuthnMethod.TEMP_CODE]: <TempcodeHex />,
      };
      switch (event) {
        case EventType.AUTHENTICATED:
          void onSuccess();
          break;
        case EventType.CANCEL:
          // Decline-consent now holds in authn and emits CANCEL; the host
          // disposes by returning the user to the start of the flow.
          restartFlow();
          break;
        case EventType.ERROR:
          // In-flow (non-terminal) outcomes are handled by authn's own UI; the
          // host only clears any lingering banner.
          if (
            errorCode === AuthnErrorCode.LIVENESS_FAILED ||
            errorCode === AuthnErrorCode.FACTOR_LOCK_PERSISTENT ||
            errorCode === AuthnErrorCode.FACTOR_LOCK_TEMPORARY ||
            errorCode === AuthnErrorCode.FACTOR_CAPTURE_FAILED
          ) {
            clearErrorNotifications();
            break;
          }
          if (errorCode === AuthnErrorCode.FACE_TIMEOUT) {
            restartFlow();
            break;
          }

          // Terminal outcomes: authn now holds an opaque surface for any
          // FatalError and delegates the terminal screen to the host. Render
          // the mapped host screen (or the generic fallback) in place of
          // <Authn> via terminalErrorCode, mirroring icp-core's errorScreens.
          if (data instanceof FatalError) {
            clearErrorNotifications();
            setTerminalErrorCode(errorCode);
            break;
          }

          if (errorCode === AuthnErrorCode.PUSH_REJECTED) {
            emitAuthnError();
            restartFlow();
            break;
          }

          if (
            errorCode === AuthnErrorCode.ACCOUNT_LOCKED ||
            errorCode === AuthnErrorCode.USER_NOT_FOUND
          ) {
            emitAuthnError();
            restartFlow();
            const { tenantId } = getTenantIdAndTargetUrl();
            if (tenantId) {
              handleSubmit();
            }
          } else if (errorCode && errorCode in errorConfiguration) {
            emitAuthnError();
          } else {
            emitError(TenantErrorCode.INTERNAL);
          }
          break;
        case EventType.CAMERA_ERROR:
          if (errorCode === AuthnErrorCode.CAMERA_IN_USE) {
            emitError(AuthnErrorCode.CAMERA_IN_USE);
          }

          break;
        case EventType.OFFLINE:
          emitError(AuthnErrorCode.OFFLINE);
          break;
        case EventType.ONLINE:
        case EventType.CLEAR_BANNER:
        case EventType.PERMISSION_GRANTED:
        case EventType.DEVICE_RECONNECTED:
          clearErrorNotifications();
          break;
        case EventType.VIEW_CHANGED:
          setActualView(data);
          break;
        case EventType.TERMINATE_SESSION:
          if (data === 'timeout') {
            restartFlow();
          }
          break;
        case EventType.TIMEOUT:
        case EventType.START_AUTHENTICATING:
          setCustomHexagonImage(data ? authMethodIcons[data] : undefined);
          break;
        // The enroll and reconsent gates (ME-11279) are face flows that narrate
        // themselves only through these events — their intro/consent screens emit
        // no VIEW_CHANGED — so without capturing them the hex would stay on the
        // glyph the preceding face scan set. Mount (data:true) shows the face-scan
        // hex for the whole gate; unmount (data:false) clears it so the next
        // screen's own event owns the glyph.
        case EventType.START_ENROLLING:
        case EventType.START_RECONSENT:
          setCustomHexagonImage(data ? <ScanningElement /> : undefined);
          break;
        case EventType.INTERNAL_ERROR:
          emitAuthnError(TenantErrorCode.UNKNOWN);
          break;
        case EventType.FINISH_AUTHENTICATING:
          if (data === AuthnMethod.PASSWORD || data === AuthnMethod.TEMP_CODE) {
            setCustomHexagonImage(<UserHex />);
          }
          break;
      }
    },
    [emitError, onSuccess, restartFlow, handleSubmit, clearErrorNotifications],
  );

  // Reset the username form after the user requests a temp code
  useEventListener(event => {
    if (event.type === SelfServiceEventTypes.ACCOUNT_RECOVERY_ACCEPTED) {
      setAuthDetails(current =>
        current ? { ...current, username: undefined } : current,
      );
    }
  });

  const featureFlags: FeatureFlagsProp = {};

  // TODO: determine based on tenantId once we decide what to do with IAD
  featureFlags.enableIAD = false;

  // Host-owned terminal screens for the FatalError outcomes authn delegates.
  // Unmapped codes fall back to the simple generic InformationDialog below.
  const networkRetryButton = {
    type: 'submit' as const,
    children: <>{t('actions.again')}</>,
    onClick: () => setTerminalErrorCode(undefined),
    disabled: !online,
  };
  const errorScreens: Record<string, React.ReactElement> = {
    [AuthnErrorCode.FACTORS_INSUFFICIENT]: <InsufficientFactors />,
    [AuthnErrorCode.NETWORK_ERROR]: (
      <InformationDialog
        text={t('errors.something-unexpected')}
        buttonProps={networkRetryButton}
      />
    ),
    [AuthnErrorCode.ERR_NETWORK]: (
      <InformationDialog
        text={t('errors.something-unexpected')}
        buttonProps={networkRetryButton}
      />
    ),
  };
  const terminalErrorScreen =
    terminalErrorCode !== undefined
      ? (errorScreens[terminalErrorCode] ?? (
          <InformationDialog text={t('errors.something-unexpected')} />
        ))
      : undefined;

  const { Content } = Layout;
  return (
    <Layout className={'tenant-lookup-window'}>
      <Content>
        <Row className="tenant-lookup-container">
          <Col xs={0} sm={0} md={12} lg={14} className="portal-info">
            <div className="tenant-lookup-info-steps">
              <h1>{t('tenant-lookup.info.title')}</h1>
              <Separator />
              <div>
                <p>{t('tenant-lookup.info.steps-1')}</p>
                <p>{t('tenant-lookup.info.steps-2')}</p>
              </div>
            </div>

            {/* Footer */}
            <div className={'tenant-lookup-info-footer'}>
              <p>
                © {new Date().getFullYear()} Imprivata, Inc. All rights
                reserved.
              </p>
            </div>
          </Col>
          <Col xs={24} sm={24} md={12} lg={10}>
            <div className={'login-container'}>
              {banner && actualView !== AuthnViews.FACE_ID && (
                <NotificationBanner
                  description={banner.description}
                  message={banner.message}
                  type={banner.type === 'error' ? 'warning' : banner.type}
                  duration={20}
                  onClose={clearErrorNotifications}
                />
              )}
              <AuthnLayout
                hexImage={customHexagonImage}
                header={header}
                logoUrl={logoUrl}
                hideLayout={
                  actualView === AuthnViews.INFORMATION_DIALOG ||
                  terminalErrorScreen !== undefined
                }
                banner={actualView === AuthnViews.FACE_ID ? banner : undefined}
                {...(banner &&
                  actualView === AuthnViews.FACE_ID && {
                    renderCustomBanner: (
                      <Banner
                        message={banner?.description}
                        title={banner?.message}
                        type={
                          banner?.type === 'error' ||
                          banner?.type === 'warning'
                            ? 'warning'
                            : 'success'
                        }
                        className="tenant-login-face-banner"
                      />
                    ),
                  })}
              >
                {terminalErrorScreen !== undefined ? (
                  terminalErrorScreen
                ) : authDetails?.applicationId ? (
                  <>
                    {!authDetails.username && (
                      <UserNameForm
                        onSubmit={username => {
                          setAuthDetails({ ...authDetails, username });
                        }}
                      />
                    )}
                    {authDetails.username && (
                      <Authn
                        tenantId={authDetails?.tenantId}
                        onEvent={handleEvent}
                        deviceFactors={deviceFactors}
                        requestConfig={requestConfig}
                        tracing={tracing}
                        applicationId={authDetails?.applicationId}
                        allowChangeUsername={!!authDetails?.applicationId}
                        featureFlags={featureFlags}
                      />
                    )}
                  </>
                ) : (
                  <LoginComponent onSubmit={handleSubmit} />
                )}
              </AuthnLayout>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export { TenantLogin };